import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { dkk, num, formatErr } from "../lib/api";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Search, Car as CarIcon, Package, Banknote, Calendar as CalIcon } from "lucide-react";
import { toast } from "sonner";

// Faste omk pr bil — ex moms pr måned (samme felter som Cars.jsx)
const FIXED_FIELDS = [
  { key: "forsikring", factor: 1 },
  { key: "vejhjaelp", factor: 1 },
  { key: "vaegtafgift", factor: 1 / 6 },
  { key: "laan", factor: 1 },
  { key: "noeglefri", factor: 1 },
  { key: "leasing", factor: 1 },
];
const EXTRA_FACTOR = { monthly: 1, halfyearly: 1 / 6, yearly: 1 / 12, once: 0 };

function monthlyCost(car) {
  let t = 0;
  FIXED_FIELDS.forEach((f) => {
    t += Number(car?.fixed_costs?.[f.key] || 0) * f.factor;
  });
  (car?.extra_costs || []).forEach((ec) => {
    // BEMÆRK: Brug ?? (nullish) — || 1 ville tolke 0 (engangs) som falsy og bruge 1 (monthly)!
    const factor = EXTRA_FACTOR[ec.frequency] ?? 1;
    const amt = Number(ec.amount || 0);
    const amtEx = ec.vat_included ? amt / 1.25 : amt;
    t += amtEx * factor;
  });
  return t;
}

function StatusBadge({ car }) {
  if (car.status === "lager") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider border border-zinc-700 text-zinc-300" data-testid="badge-lager">
        <Package className="w-3 h-3" /> Lager
      </span>
    );
  }
  const plat = car.platform || "gomore";
  if (plat === "carbuddi") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider border border-[#C8A661]/60 text-[#C8A661]" data-testid="badge-carbuddi">
        <Banknote className="w-3 h-3" /> Carbuddi
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider border border-emerald-700/60 text-emerald-400" data-testid="badge-gomore">
      <CarIcon className="w-3 h-3" /> GoMore
    </span>
  );
}

function linkFor(car) {
  if (car.status === "lager") return "/admin/lager";
  if ((car.platform || "gomore") === "carbuddi") return "/admin/carbuddi-biler";
  return "/admin/biler";
}

export default function AllCars() {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | gomore | carbuddi | lager
  const [withVat, setWithVat] = useState(true);
  const [subtractFixed, setSubtractFixed] = useState(false);
  // Belægningsgrad-fane (vises som standard, kan slås fra)
  const [showOccupancy, setShowOccupancy] = useState(true);
  const [occMonths, setOccMonths] = useState(12);
  const [occHistory, setOccHistory] = useState({ months: [], cars: [] });
  const [occLoading, setOccLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/cars", { params: { include_revenue: true } })
      .then((cRes) => {
        const arr = (cRes.data || []).filter((c) => c.status !== "sold" && c.status !== "formidling");
        setCars(arr);
      })
      .catch((e) => toast.error(formatErr(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!showOccupancy) return;
    setOccLoading(true);
    api.get("/occupancy/history", { params: { months: occMonths } })
      .then((r) => {
        // Kun aktive GoMore-biler er relevante i belægnings-heatmap.
        // Lager-biler og Carbuddi-biler filtreres væk.
        const filteredCars = (r.data.cars || []).filter(
          (c) => c.platform === "gomore" && c.status !== "lager"
        );
        setOccHistory({ ...r.data, cars: filteredCars });
      })
      .catch((e) => toast.error(formatErr(e)))
      .finally(() => setOccLoading(false));
  }, [showOccupancy, occMonths]);

  // Byg map af belægnings-historik pr. license_plate for hurtig lookup
  const occByPlate = useMemo(() => {
    const m = {};
    (occHistory.cars || []).forEach((o) => { if (o.license_plate) m[o.license_plate.toUpperCase()] = o; });
    return m;
  }, [occHistory]);

  const monthShort = (ym) => {
    const [y, mo] = ym.split("-");
    const names = ["Jan","Feb","Mar","Apr","Maj","Jun","Jul","Aug","Sep","Okt","Nov","Dec"];
    return `${names[parseInt(mo,10)-1]} ${y.slice(2)}`;
  };
  const cellColor = (pct) => {
    if (pct >= 90) return "bg-emerald-500 text-white";
    if (pct >= 70) return "bg-emerald-600/80 text-white";
    if (pct >= 40) return "bg-[#C8A661] text-zinc-900";
    if (pct >= 15) return "bg-amber-700/70 text-white";
    if (pct > 0) return "bg-amber-800/50 text-amber-200";
    return "bg-zinc-800 text-zinc-600";
  };

  const overallAvg = useMemo(() => {
    if (!occHistory.cars || !occHistory.cars.length) return 0;
    // Kun biler med mindst én måned der tælles med (>= 15%)
    const eligibleCars = occHistory.cars.filter((c) => (c.months_counted || 0) > 0);
    if (!eligibleCars.length) return 0;
    const sum = eligibleCars.reduce((s, c) => s + (c.avg_pct || 0), 0);
    return Math.round(sum / eligibleCars.length * 10) / 10;
  }, [occHistory]);

  const copyPlate = async (plate) => {
    if (!plate) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(plate);
      } else {
        // Fallback (ældre browsere / non-https)
        const ta = document.createElement("textarea");
        ta.value = plate;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success(`${plate} kopieret`);
    } catch {
      toast.error("Kunne ikke kopiere — kopiér manuelt");
    }
  };

  // Sum carbuddi-indtjening pr. bil var tidligere brugt til at lægge oveni — fjernet pga. dobbelttælling.
  // Backend's total_rental_revenue inkluderer allerede både GoMore + Carbuddi.

  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    return cars
      .filter((c) => {
        if (filter === "lager") return c.status === "lager";
        if (filter === "gomore") return c.status === "active" && (c.platform || "gomore") === "gomore";
        if (filter === "carbuddi") return c.status === "active" && (c.platform || "gomore") === "carbuddi";
        return true;
      })
      .filter((c) =>
        !s
          ? true
          : c.license_plate?.toLowerCase().includes(s) ||
            c.make?.toLowerCase().includes(s) ||
            c.model?.toLowerCase().includes(s)
      )
      .sort((a, b) => (a.license_plate || "").localeCompare(b.license_plate || ""));
  }, [cars, search, filter]);

  const counts = useMemo(() => {
    let g = 0, c = 0, l = 0;
    cars.forEach((x) => {
      if (x.status === "lager") l++;
      else if (x.status === "active") {
        if ((x.platform || "gomore") === "carbuddi") c++;
        else g++;
      }
    });
    return { g, c, l, total: g + c + l };
  }, [cars]);

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-7xl" data-testid="all-cars-page">
      <div className="mb-6 sm:mb-10">
        <div className="overline text-zinc-400">Biler</div>
        <h1 className="headline text-3xl sm:text-4xl lg:text-5xl mt-2">Alle biler.</h1>
        <p className="text-zinc-500 text-xs sm:text-sm mt-1">
          Hurtigt overblik over hele flåden — GoMore, Carbuddi og Lager. Klik en bil for at gå til den korrekte fane.
        </p>
      </div>

      {/* Belægningsgrad-fane */}
      <div className="section-card p-3 sm:p-4 mb-4" data-testid="all-cars-occupancy">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Switch
              checked={showOccupancy}
              onCheckedChange={setShowOccupancy}
              id="ac-toggle-occ"
              data-testid="ac-toggle-occ"
              className="data-[state=checked]:bg-[#C8A661]"
            />
            <Label htmlFor="ac-toggle-occ" className="cursor-pointer">
              <div className="font-medium text-sm flex items-center gap-2"><CalIcon className="w-4 h-4"/> Vis belægningsgrad</div>
              <div className="text-xs text-zinc-500">Månedlig belægning pr. bil — GoMore fra CSV, Carbuddi fra manuel indtjening.</div>
            </Label>
          </div>
          {showOccupancy && (
            <div className="flex items-center gap-3">
              <Label htmlFor="occ-months" className="text-xs text-zinc-500">Periode:</Label>
              <select
                id="occ-months"
                value={occMonths}
                onChange={(e) => setOccMonths(parseInt(e.target.value, 10))}
                className="bg-zinc-800 border border-zinc-700 text-zinc-200 px-3 py-2 text-sm rounded-none"
                data-testid="occ-months-select"
              >
                <option value="6">Seneste 6 måneder</option>
                <option value="12">Seneste 12 måneder</option>
                <option value="24">Seneste 24 måneder</option>
              </select>
              <div className="text-xs text-zinc-400 tabular-nums" data-testid="occ-summary">
                Gns. flåde-belægning: <span className="text-[#C8A661] font-semibold">{overallAvg}%</span>
              </div>
            </div>
          )}
        </div>

        {/* Heatmap-visning inline (hvis aktiveret) */}
        {showOccupancy && (
          <div className="mt-4 overflow-x-auto" data-testid="occ-heatmap">
            {occLoading ? (
              <div className="py-8 text-center text-zinc-500 text-sm">Indlæser belægningsdata…</div>
            ) : occHistory.cars.length === 0 ? (
              <div className="py-8 text-center text-zinc-500 text-sm">Ingen data</div>
            ) : (
              <table className="w-full min-w-[720px]">
                <thead>
                  <tr className="text-zinc-500 border-b border-zinc-800 text-xs">
                    <th className="text-left py-2 pr-4 font-normal">Bil</th>
                    <th className="text-right py-2 pr-4 font-normal">Gns.</th>
                    {occHistory.months.map((ym) => (
                      <th key={ym} className="py-2 px-1 font-normal text-center whitespace-nowrap">{monthShort(ym)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {occHistory.cars.map((car) => (
                    <tr key={car.car_id} className="border-b border-zinc-900/50" data-testid={`occ-row-${car.license_plate}`}>
                      <td className="py-2 pr-4">
                        <div className="font-mono text-[#C8A661] font-semibold text-base">{car.license_plate}</div>
                        <div className="text-zinc-600 text-xs">{car.make} {car.model}</div>
                      </td>
                      <td className={`text-right py-2 pr-4 tabular-nums font-bold text-lg ${car.avg_pct >= 70 ? "text-emerald-400" : car.avg_pct >= 40 ? "text-[#C8A661]" : car.avg_pct > 0 ? "text-amber-500" : "text-zinc-600"}`}>
                        {car.avg_pct}%
                        {car.months_counted !== undefined && car.months_counted < car.months_total && (
                          <div className="text-[10px] text-zinc-500 font-normal mt-0.5">
                            {car.months_counted}/{car.months_total} md.
                          </div>
                        )}
                      </td>
                      {occHistory.months.map((ym) => {
                        const mo = car.by_month?.[ym];
                        const pct = mo?.occupancy_pct ?? 0;
                        return (
                          <td key={ym} className="p-0.5 text-center">
                            <div
                              className={`inline-flex flex-col items-center justify-center w-full min-w-[52px] h-12 text-sm font-bold tabular-nums rounded ${cellColor(pct)}`}
                              title={`${monthShort(ym)}: ${pct}% (${mo?.days_rented || 0}/${mo?.days_in_month || 0} dage)`}
                              data-testid={`occ-cell-${car.license_plate}-${ym}`}
                            >
                              <div>{pct}%</div>
                              {mo?.rentals > 0 && <div className="text-[10px] font-normal opacity-75">{mo.rentals}×</div>}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Toggles — moms + træk faste omk fra */}
      <div className="section-card p-3 sm:p-4 mb-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="all-cars-toggles">
        <div className="flex items-center gap-3">
          <Switch
            checked={withVat}
            onCheckedChange={setWithVat}
            id="ac-toggle-vat"
            data-testid="ac-toggle-vat"
            className="data-[state=checked]:bg-[#C8A661]"
          />
          <Label htmlFor="ac-toggle-vat" className="cursor-pointer">
            <div className="font-medium text-sm">Med moms</div>
            <div className="text-xs text-zinc-500">{withVat ? "Inkl. 25% moms" : "Ex. moms"}</div>
          </Label>
        </div>
        <div className="flex items-center gap-3">
          <Switch
            checked={subtractFixed}
            onCheckedChange={setSubtractFixed}
            id="ac-toggle-fixed"
            data-testid="ac-toggle-fixed"
            className="data-[state=checked]:bg-[#C8A661]"
          />
          <Label htmlFor="ac-toggle-fixed" className="cursor-pointer">
            <div className="font-medium text-sm">Træk månedlige faste omk. fra</div>
            <div className="text-xs text-zinc-500">{subtractFixed ? "Indtjening − omk. × antal mdr." : "Brutto indtjening"}</div>
          </Label>
        </div>
      </div>

      {/* Filter chips + count */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="all-cars-filters">
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")} testid="filter-all">
          Alle <span className="text-zinc-500">· {counts.total}</span>
        </FilterChip>
        <FilterChip active={filter === "gomore"} onClick={() => setFilter("gomore")} testid="filter-gomore">
          GoMore <span className="text-zinc-500">· {counts.g}</span>
        </FilterChip>
        <FilterChip active={filter === "carbuddi"} onClick={() => setFilter("carbuddi")} testid="filter-carbuddi">
          Carbuddi <span className="text-zinc-500">· {counts.c}</span>
        </FilterChip>
        <FilterChip active={filter === "lager"} onClick={() => setFilter("lager")} testid="filter-lager">
          Lager <span className="text-zinc-500">· {counts.l}</span>
        </FilterChip>
      </div>

      <div className="relative mb-4 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input
          placeholder="Søg på nummerplade, mærke eller model"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 rounded-none border-zinc-700"
          data-testid="all-cars-search"
        />
      </div>

      <div className="section-card overflow-x-auto">
        <table className="w-full dense min-w-[820px]" data-testid="all-cars-table">
          <thead className="table-head">
            <tr>
              <th>Nummerplade</th>
              <th>Status</th>
              <th className="text-zinc-500 font-normal text-xs">Bil</th>
              <th className="text-zinc-500 font-normal text-xs">Årgang</th>
              <th className="text-zinc-500 font-normal text-xs">Km nu</th>
              <th className="text-right">Købspris</th>
              <th className="text-right">Står dig i</th>
              <th className="text-right">Samlet indtjening</th>
              {showOccupancy && <th className="text-right">Gns. belægning</th>}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={showOccupancy ? 10 : 9} className="p-8 text-center text-zinc-400">Indlæser…</td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={showOccupancy ? 10 : 9} className="p-8 text-center text-zinc-400">Ingen biler matcher.</td>
              </tr>
            )}
            {filtered.map((c) => {
              const fixedMonthlyEx = monthlyCost(c); // ex moms pr. md
              const months = Number(c.active_months_count || 0);
              // Backend's total_rental_revenue inkluderer ALLEREDE både GoMore + Carbuddi (inkl. moms).
              // Carbuddi-tabellen carbuddiTotalByCarId beholdes til evt. fremtidig split-visning,
              // men må IKKE lægges oveni igen — det giver dobbelttælling for Carbuddi-biler.
              const earnInclMoms = Number(c.total_rental_revenue || 0);
              const earnExMoms = earnInclMoms / 1.25;
              const earnDisplayed = withVat ? earnInclMoms : earnExMoms;
              const fixedDisplayedTotal = (withVat ? fixedMonthlyEx * 1.25 : fixedMonthlyEx) * months;
              const finalDisplayed = subtractFixed ? earnDisplayed - fixedDisplayedTotal : earnDisplayed;
              const positive = finalDisplayed > 0;

              // "Står dig i" = Samlet indtjening (ex moms) − købspris − klargøring − engangs-omkostninger − løbende omk. × antal måneder
              // BEMÆRK: Månedlige/halvårlige/årlige ekstra omkostninger er ALLEREDE indeholdt i
              // `fixedMonthlyEx` (via calcMonthlyCost → EXTRA_FREQ.factor). Derfor må vi KUN summere
              // engangs-omkostninger (frequency === "once") separat her, ellers dobbelt-tælles de.
              const PREP = 3500;
              const purchase = Number(c.purchase_price || 0);
              const extrasOnceEx = (c.extra_costs || [])
                .filter((ec) => ec.frequency === "once")
                .reduce((s, ec) => {
                  const amt = Number(ec.amount || 0);
                  return s + (ec.vat_included ? amt / 1.25 : amt);
                }, 0);
              const totalCost = purchase ? purchase + PREP + extrasOnceEx + (fixedMonthlyEx * months) : 0;
              const balance = purchase ? earnExMoms - totalCost : null; // positive = nettoprofit
              const paidBack = balance !== null && balance >= 0;

              const href = linkFor(c);
              return (
                <tr
                  key={c.id}
                  className="table-row hover:bg-[#262626] cursor-pointer transition-colors"
                  data-testid={`all-car-row-${c.license_plate}`}
                >
                  <td className="font-display font-bold tracking-tight">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); copyPlate(c.license_plate); }}
                      className="hover:text-[#C8A661] active:text-[#D4B574] transition-colors cursor-pointer select-all"
                      title="Klik for at kopiere nummerpladen"
                      data-testid={`all-car-plate-${c.license_plate}`}
                    >
                      {c.license_plate}
                    </button>
                  </td>
                  <td>
                    <StatusBadge car={c} />
                  </td>
                  <td className="text-zinc-500 text-xs">
                    {c.make || c.model ? (
                      <div>
                        <div>{c.make}</div>
                        <div className="text-xs text-zinc-600">{c.model}</div>
                      </div>
                    ) : (
                      <span className="text-red-600 text-xs font-semibold">Mangler data</span>
                    )}
                  </td>
                  <td className="text-xs text-zinc-500">{c.year || "—"}</td>
                  <td className="text-xs tabular-nums text-zinc-500">
                    {c.current_km != null ? num(c.current_km) : <span className="text-red-600">Mangler</span>}
                  </td>
                  <td className="text-right tabular-nums text-zinc-300">
                    {c.purchase_price ? dkk(c.purchase_price) : <span className="text-zinc-700">—</span>}
                  </td>
                  <td className={"text-right tabular-nums font-semibold " + (balance === null ? "text-zinc-700" : balance >= 0 ? "text-emerald-400" : "text-red-400")}
                      data-testid={`all-car-balance-${c.license_plate}`}>
                    {balance === null
                      ? "—"
                      : (balance >= 0 ? "+" : "") + dkk(balance)}
                    {balance !== null && (
                      <div className="text-[10px] text-zinc-600 font-normal mt-0.5">
                        Indtj. − køb − omk.
                      </div>
                    )}
                  </td>
                  <td className={"text-right tabular-nums font-semibold " + (finalDisplayed === 0 ? "text-zinc-700" : positive ? "text-emerald-400" : "text-red-400")}
                      data-testid={`all-car-earnings-${c.license_plate}`}>
                    {finalDisplayed === 0 ? "—" : dkk(finalDisplayed)}
                    {subtractFixed && months > 0 && fixedMonthlyEx > 0 && (
                      <div className="text-[10px] text-zinc-600 font-normal mt-0.5">
                        {months} md · {dkk(withVat ? fixedMonthlyEx * 1.25 : fixedMonthlyEx)}/md
                      </div>
                    )}
                  </td>
                  {showOccupancy && (() => {
                    const o = occByPlate[(c.license_plate || "").toUpperCase()];
                    if (occLoading) return <td className="text-right text-xs text-zinc-600">…</td>;
                    if (!o) return <td className="text-right text-xs text-zinc-700">—</td>;
                    const pct = o.avg_pct || 0;
                    const barColor = pct >= 70 ? "bg-emerald-500" : pct >= 40 ? "bg-[#C8A661]" : pct > 0 ? "bg-amber-600" : "bg-zinc-700";
                    return (
                      <td className="text-right" data-testid={`all-car-occupancy-${c.license_plate}`}>
                        <div className="flex items-center justify-end gap-2">
                          <div className={"font-semibold tabular-nums " + (pct >= 70 ? "text-emerald-400" : pct >= 40 ? "text-[#C8A661]" : pct > 0 ? "text-amber-500" : "text-zinc-600")}>
                            {pct}%
                          </div>
                          <div className="w-12 h-2 bg-zinc-800 relative overflow-hidden rounded">
                            <div className={`absolute top-0 left-0 h-full ${barColor} transition-all`} style={{ width: `${Math.min(pct, 100)}%` }} />
                          </div>
                        </div>
                      </td>
                    );
                  })()}
                  <td className="text-right">
                    <Link
                      to={href}
                      className="text-[#C8A661] text-xs uppercase tracking-wider hover:text-[#D4B574]"
                      data-testid={`all-car-open-${c.license_plate}`}
                    >
                      Åbn →
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors " +
        (active
          ? "bg-[#C8A661] text-black border-[#C8A661]"
          : "bg-transparent text-zinc-300 border-zinc-700 hover:border-zinc-500")
      }
      data-testid={testid}
    >
      {children}
    </button>
  );
}
